import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, Textarea, Badge, Modal } from '@/backoffice/components/ui';
import {
  fetchClientAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  type Address,
} from '@/domains/client/services/client.service';
import {
  MapPin,
  Plus,
  Star,
  Pencil,
  Trash2,
  Loader2,
} from 'lucide-react';

const emptyForm = {
  nom_adresse: '',
  adresse_complete: '',
  point_repere: '',
  quartier: '',
  ville: 'Conakry',
  pays: 'Guinée',
  code_postal: '',
  est_principale: false,
};

export function AddressesPage() {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);
  const [form, setForm] = useState(emptyForm);

  const loadAddresses = () => {
    setLoading(true);
    fetchClientAddresses()
      .then(setAddresses)
      .catch(() => setError('Impossible de charger vos adresses.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAddresses();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
    setError(null);
  };

  const openEdit = (address: Address) => {
    setEditing(address);
    setForm({
      nom_adresse: address.nom_adresse,
      adresse_complete: address.adresse_complete,
      point_repere: address.point_repere ?? '',
      quartier: address.quartier ?? '',
      ville: address.ville ?? '',
      pays: address.pays ?? '',
      code_postal: address.code_postal ?? '',
      est_principale: address.est_principale,
    });
    setShowModal(true);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!form.nom_adresse.trim() || !form.adresse_complete.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await updateAddress(editing.id, form);
      } else {
        await createAddress(form);
      }
      setShowModal(false);
      loadAddresses();
    } catch {
      setError('Échec de l\'enregistrement de l\'adresse.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (address: Address) => {
    if (!confirm(`Supprimer l'adresse « ${address.nom_adresse} » ?`)) return;
    try {
      await deleteAddress(address.id);
      setAddresses((prev) => prev.filter((a) => a.id !== address.id));
    } catch {
      setError('Impossible de supprimer cette adresse.');
    }
  };

  const handleSetPrimary = async (address: Address) => {
    try {
      await updateAddress(address.id, { est_principale: true });
      loadAddresses();
    } catch {
      setError('Impossible de définir l\'adresse principale.');
    }
  };

  return (
    <DashboardLayout userType="client" title="Mes adresses">
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-600 text-sm">
          Enregistrez vos adresses pour préremplir rapidement vos commandes.
        </p>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={openCreate}>
          Nouvelle adresse
        </Button>
      </div>

      {error && !showModal && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Chargement...
        </div>
      ) : addresses.length === 0 ? (
        <Card className="text-center py-12">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">Aucune adresse enregistrée</p>
          <Button onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            Ajouter une adresse
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {addresses.map((address) => (
            <Card key={address.id} className="relative">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-teal-600 shrink-0" />
                  <h3 className="font-semibold text-gray-900">{address.nom_adresse}</h3>
                </div>
                {address.est_principale && (
                  <Badge variant="success" size="sm">Principale</Badge>
                )}
              </div>

              <p className="text-gray-700 mb-2">{address.adresse_complete}</p>
              {address.point_repere && (
                <p className="text-sm text-gray-500 mb-1">Repère : {address.point_repere}</p>
              )}
              <p className="text-sm text-gray-500">
                {[address.quartier, address.ville, address.pays].filter(Boolean).join(', ')}
              </p>

              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                {!address.est_principale && (
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Star className="w-4 h-4" />}
                    onClick={() => handleSetPrimary(address)}
                  >
                    Définir principale
                  </Button>
                )}
                <Button size="sm" variant="outline" leftIcon={<Pencil className="w-4 h-4" />} onClick={() => openEdit(address)}>
                  Modifier
                </Button>
                <Button size="sm" variant="danger" leftIcon={<Trash2 className="w-4 h-4" />} onClick={() => handleDelete(address)}>
                  Supprimer
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Modifier l\'adresse' : 'Nouvelle adresse'}
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}
        <div className="space-y-4">
          <Input
            label="Nom de l'adresse"
            value={form.nom_adresse}
            onChange={(e) => setForm({ ...form, nom_adresse: e.target.value })}
            placeholder="Ex: Bureau, Domicile..."
          />
          <Textarea
            label="Adresse complète"
            value={form.adresse_complete}
            onChange={(e) => setForm({ ...form, adresse_complete: e.target.value })}
            placeholder="Rue, immeuble, numéro..."
          />
          <Input
            label="Point de repère"
            value={form.point_repere}
            onChange={(e) => setForm({ ...form, point_repere: e.target.value })}
            placeholder="Ex: En face de la pharmacie..."
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Quartier"
              value={form.quartier}
              onChange={(e) => setForm({ ...form, quartier: e.target.value })}
            />
            <Input
              label="Ville"
              value={form.ville}
              onChange={(e) => setForm({ ...form, ville: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Pays"
              value={form.pays}
              onChange={(e) => setForm({ ...form, pays: e.target.value })}
            />
            <Input
              label="Code postal"
              value={form.code_postal}
              onChange={(e) => setForm({ ...form, code_postal: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.est_principale}
              onChange={(e) => setForm({ ...form, est_principale: e.target.checked })}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            Définir comme adresse principale
          </label>
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
            Annuler
          </Button>
          <Button
            className="flex-1"
            onClick={handleSubmit}
            isLoading={saving}
            disabled={!form.nom_adresse.trim() || !form.adresse_complete.trim()}
          >
            {editing ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
